import React from 'react'
import { Switch } from '../../components/switch'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UISwitchProps extends React.ComponentProps<typeof Switch> {
  label?: string
  labelId?: string
  description?: string
  placement?: 'left' | 'right'
  'aria-label'?: string
}

/**
 * 스위치 컴포넌트
 * 라벨과 설명을 통합하여 더 나은 사용자 경험을 제공합니다.
 */
export const UISwitch = React.forwardRef<
  React.ComponentRef<typeof Switch>,
  UISwitchProps
>(({ className, label, labelId, description, placement = 'right', 'aria-label': ariaLabel, ...props }, ref) => {
  if (!label && !description) {
    return (
      <Switch
        ref={ref}
        className={cn(className)}
        aria-label={ariaLabel}
        {...props}
      />
    )
  }

  const labelElement = label && (
    <UILabel
      htmlFor={labelId}
      className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 "
    >
      {label}
    </UILabel>
  )

  const descriptionElement = description && (
    <UITypography variant="p" size="sm" color="muted">{description}</UITypography>
  )

  const switchElement = (
    <Switch
      ref={ref}
      id={labelId}
      className={cn(className)}
      aria-label={ariaLabel}
      {...props}
    />
  )

  return (
    <UIBox className={cn(
      "flex space-x-3",
      description ? "items-start" : "items-center"
    )}>
      {placement === 'left' && (
        <UIBox>
          {switchElement}
        </UIBox>
      )}
      <UIBox className="space-y-1 flex-1">
        {labelElement}
        {descriptionElement}
      </UIBox>
      {placement === 'right' && (
        <UIBox>
          {switchElement}
        </UIBox>
      )}
    </UIBox>
  )
})

UISwitch.displayName = "UISwitch"